/*
  Bark & Bitter mobile menu and search panel (sections/bb-mobile-menu.liquid)

  - Takes over the header's hamburger on phones and tablets and opens the menu
    as a modal <dialog>. A modal dialog sits in the browser's top layer, so app
    widgets (like the "Get 10% off" teaser) can't sit on top of it, and the
    browser handles Escape, focus trapping and returning focus.
  - Takes over the header's search icon too, opening the same panel in search
    mode with the cursor in the search box.
  - Search mode shows suggestions before typing and live results as you type,
    rendered by sections/bb-predictive-search.liquid through Shopify's
    predictive search: products first, then recipes, blog posts and pages.

  The theme's own drawers are left untouched. If this script doesn't run, or
  the browser has no <dialog> support, the theme's drawers still open as before.
*/
(() => {
  if (customElements.get('bb-mobile-menu')) return;

  // The theme shows its mobile header (hamburger and search icon) below 1025px.
  const MOBILE_QUERY = '(max-width: 1024px)';
  // .header-mobile is on the mobile header whatever its sticky setting. The
  // <sticky-header-mobile> element only exists while sticky is on.
  const HEADER_CART_SELECTOR = '.header-mobile .header__icon--cart';
  const HEADER_CART_COUNT_SELECTOR = '.header-mobile .cart-count-bubble';
  const RESULTS_SECTION = 'bb-predictive-search';
  const SEARCH_DELAY = 200;

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class BbMobileMenu extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;

      this.sectionId = this.dataset.sectionId;
      this.toggleSelector = this.dataset.toggle || '.mobileMenu-toggle, [data-mobile-menu]';
      this.searchTriggerSelector = this.dataset.searchTrigger || '';
      this.predictiveUrl = this.dataset.predictiveUrl || '/search/suggest';

      this.searchForm = this.querySelector('[data-bbm-search-form]');
      this.searchInput = this.querySelector('[data-bbm-search-input]');
      this.searchView = this.querySelector('[data-bbm-search-view]');
      this.searchResults = this.querySelector('[data-bbm-results]');
      this.searchStatus = this.querySelector('[data-bbm-search-status]');

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
      this.onDialogClick = this.onDialogClick.bind(this);
      this.onDialogPointerDown = this.onDialogPointerDown.bind(this);
      this.onCancel = this.onCancel.bind(this);
      this.onClose = this.onClose.bind(this);
      this.onSectionSelect = this.onSectionSelect.bind(this);
      this.onSectionDeselect = this.onSectionDeselect.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);

      // Capture phase on document runs before the theme's own handlers, so the
      // theme's menu and search drawers never open while this panel is enabled.
      document.addEventListener('click', this.onDocumentClick, true);
      document.addEventListener('pointerdown', this.onDocumentPointerDown, true);

      this.dialog.addEventListener('pointerdown', this.onDialogPointerDown);
      this.dialog.addEventListener('click', this.onDialogClick);
      this.dialog.addEventListener('cancel', this.onCancel);
      this.dialog.addEventListener('close', this.onClose);

      if (this.searchInput) {
        this.searchInput.addEventListener('focus', () => this.setMode('search'));
        this.searchInput.addEventListener('input', () => this.onSearchInput());
      }

      // Theme editor: show the panel while its section or a block is selected.
      document.addEventListener('shopify:section:select', this.onSectionSelect);
      document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      // Safety net. The theme opens its old drawer by adding body.menu_open
      // (sections/header-mobile.liquid, plus handlers in assets/theme.js). If any
      // of those still fires on a phone or tablet, undo it and open this menu
      // instead. Mutation observers run before the next paint, so the old drawer
      // never flashes.
      this.bodyObserver = new MutationObserver(() => this.onBodyClassChange());
      this.bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

      // Takes the old drawers out of the page while this panel is active
      // (see html.bbm-enabled and html.bbm-search-enabled in bb-mobile-menu.css).
      this.takesOverSearch = !!(this.searchTriggerSelector && this.searchInput);
      document.documentElement.classList.add('bbm-enabled');
      document.documentElement.classList.toggle('bbm-search-enabled', this.takesOverSearch);

      this.setToggleState(false);

      // A tap before this script ran may already have opened the old drawer.
      // The observer only sees later changes, so hand that one over now.
      this.onBodyClassChange();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick, true);
      document.removeEventListener('pointerdown', this.onDocumentPointerDown, true);
      document.removeEventListener('shopify:section:select', this.onSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.bodyObserver) this.bodyObserver.disconnect();
      if (this.dialog) this.cancelPendingClose();
      if (this.searchAbort) this.searchAbort.abort();
      window.clearTimeout(this.searchTimer);
      document.documentElement.classList.remove('bbm-lock', 'bbm-enabled', 'bbm-search-enabled');
    }

    get isMobile() {
      return window.matchMedia(MOBILE_QUERY).matches;
    }

    // ---- Opening and closing -------------------------------------------------

    matchTrigger(target) {
      if (!(target instanceof Element)) return null;
      const toggle = target.closest(this.toggleSelector);
      if (toggle) return { el: toggle, mode: 'menu' };
      if (this.searchTriggerSelector && this.searchInput) {
        const search = target.closest(this.searchTriggerSelector);
        if (search) return { el: search, mode: 'search' };
      }
      return null;
    }

    setToggleState(expanded) {
      document.querySelectorAll(this.toggleSelector).forEach((toggle) => {
        toggle.setAttribute('aria-controls', this.dialog.id);
        toggle.setAttribute('aria-expanded', String(expanded));
        toggle.setAttribute('aria-haspopup', 'dialog');
      });
    }

    onDocumentClick(event) {
      const trigger = this.matchTrigger(event.target);
      if (!trigger || !this.isMobile) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.open({ mode: trigger.mode });
    }

    // Start loading the panel's images as soon as a finger lands on the
    // hamburger, so photos are ready when the panel slides in.
    onDocumentPointerDown(event) {
      if (this.imagesPrimed || !this.matchTrigger(event.target)) return;
      this.imagesPrimed = true;
      this.dialog.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
    }

    onBodyClassChange() {
      // Desktop keeps the theme's own drawers.
      if (!this.isMobile) return;
      const body = document.body.classList;
      if (body.contains('menu_open')) {
        body.remove('menu_open');
        this.open();
      }
      // The theme opens its search sidebar with body.open_search_mobile.
      if (this.takesOverSearch && body.contains('open_search_mobile')) {
        body.remove('open_search_mobile');
        this.open({ mode: 'search' });
      }
    }

    open({ instant = false, mode = 'menu' } = {}) {
      if (this.dialog.open) {
        // Reopened while the close animation was still running.
        if (this.pendingClose) {
          this.cancelPendingClose();
          this.dialog.classList.add('is-open');
        }
        this.setMode(mode);
        if (mode === 'search') this.focusSearch();
        return;
      }
      this.openedForSearch = mode === 'search';
      this.setMode(mode);
      this.syncCartCount();
      document.documentElement.classList.add('bbm-lock');
      this.dialog.classList.toggle('bbm--instant', instant || reducedMotion());
      this.dialog.showModal();
      const panel = this.dialog.querySelector('.bbm__panel');
      if (panel) panel.scrollTop = 0;
      // Focus inside the same tap, so iOS shows the keyboard.
      if (mode === 'search') this.focusSearch();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Skip if the panel was closed, or started closing, in the meantime.
          if (this.dialog.open && !this.pendingClose) this.dialog.classList.add('is-open');
        });
      });
      this.setToggleState(true);
    }

    close({ instant = false } = {}) {
      if (!this.dialog.open || this.pendingClose) return;
      const finish = () => {
        this.cancelPendingClose();
        if (this.dialog.open) this.dialog.close();
      };
      if (instant || reducedMotion() || this.dialog.classList.contains('bbm--instant')) {
        finish();
        return;
      }
      const onTransitionEnd = (event) => {
        if (event.target === this.dialog && event.propertyName === 'transform') finish();
      };
      this.pendingClose = {
        onTransitionEnd,
        timer: window.setTimeout(finish, 450),
      };
      this.dialog.addEventListener('transitionend', onTransitionEnd);
      this.dialog.classList.remove('is-open');
    }

    cancelPendingClose() {
      if (!this.pendingClose) return;
      this.dialog.removeEventListener('transitionend', this.pendingClose.onTransitionEnd);
      window.clearTimeout(this.pendingClose.timer);
      this.pendingClose = null;
    }

    onCancel(event) {
      // Escape key: animate out instead of disappearing instantly.
      event.preventDefault();
      this.close();
    }

    onClose() {
      this.cancelPendingClose();
      this.dialog.classList.remove('is-open', 'bbm--instant');
      document.documentElement.classList.remove('bbm-lock');
      this.setToggleState(false);
      this.resetSearch();
    }

    onDialogPointerDown(event) {
      this.pressStartedOnBackdrop = event.target === this.dialog;
    }

    onDialogClick(event) {
      const target = event.target;

      // A click on the dialog element itself is a click on the backdrop
      // (only reachable on tablets, where the panel is narrower than the screen).
      // Only close if the press started there too, so a drag that ends on the
      // backdrop (selecting text in the search box, say) leaves the panel open.
      if (target === this.dialog) {
        if (this.pressStartedOnBackdrop) this.close();
        return;
      }

      if (target.closest('[data-bbm-close]')) {
        this.close();
        return;
      }

      if (target.closest('[data-bbm-search-clear]')) {
        this.searchInput.value = '';
        this.onSearchInput();
        this.focusSearch();
        return;
      }

      if (target.closest('[data-bbm-search-cancel]')) {
        if (this.openedForSearch) {
          this.close();
        } else {
          this.resetSearch();
          // Cancel hides itself, so give focus somewhere to land. Not the search
          // box: focusing it would switch straight back to search.
          this.dialog.querySelector('[data-bbm-close]')?.focus({ preventScroll: true });
        }
        return;
      }

      // Suggested searches fill the box and search in place.
      const term = target.closest('[data-bbm-term]');
      if (term && this.searchInput) {
        event.preventDefault();
        this.searchInput.value = term.dataset.bbmTerm;
        this.onSearchInput({ immediate: true });
        return;
      }

      // Cart: close the panel and hand over to the header's cart icon, so the
      // cart opens exactly as it does from the header.
      const cartLink = target.closest('[data-bbm-cart]');
      const headerCart = document.querySelector(HEADER_CART_SELECTOR);
      if (cartLink && headerCart) {
        event.preventDefault();
        this.close({ instant: true });
        headerCart.click();
      }
    }

    syncCartCount() {
      const bubble = document.querySelector(HEADER_CART_COUNT_SELECTOR);
      if (!bubble) return;
      // The bubble also holds a visually hidden "0 items" label, so read only
      // the visible count.
      const source = bubble.querySelector('[data-cart-count]') || bubble;
      const match = source.textContent.match(/\d+/);
      if (!match) return;
      this.querySelectorAll('[data-bbm-cart-count]').forEach((el) => {
        el.textContent = match[0];
      });
    }

    // ---- Search --------------------------------------------------------------

    setMode(mode) {
      const search = mode === 'search' && !!this.searchInput;
      this.dialog.dataset.mode = search ? 'search' : 'menu';
      if (search) {
        const panel = this.dialog.querySelector('.bbm__panel');
        if (panel) panel.scrollTop = 0;
      }
    }

    focusSearch() {
      if (!this.searchInput) return;
      this.searchInput.focus({ preventScroll: true });
    }

    resetSearch() {
      if (!this.searchInput) return;
      window.clearTimeout(this.searchTimer);
      if (this.searchAbort) this.searchAbort.abort();
      this.searchInput.value = '';
      this.searchInput.blur();
      this.lastQuery = '';
      if (this.searchResults) this.searchResults.replaceChildren();
      if (this.searchView) delete this.searchView.dataset.hasQuery;
      if (this.searchForm) this.searchForm.classList.remove('has-value');
      if (this.searchStatus) this.searchStatus.textContent = '';
      this.setMode('menu');
    }

    onSearchInput({ immediate = false } = {}) {
      const query = this.searchInput.value.trim();
      this.searchForm.classList.toggle('has-value', this.searchInput.value.length > 0);
      window.clearTimeout(this.searchTimer);

      if (!query) {
        if (this.searchAbort) this.searchAbort.abort();
        this.lastQuery = '';
        delete this.searchView.dataset.hasQuery;
        this.searchResults.replaceChildren();
        this.searchStatus.textContent = '';
        return;
      }

      this.searchView.dataset.hasQuery = '';
      if (query === this.lastQuery) return;
      if (immediate) {
        this.fetchResults(query);
      } else {
        this.searchTimer = window.setTimeout(() => this.fetchResults(query), SEARCH_DELAY);
      }
    }

    async fetchResults(query) {
      if (this.searchAbort) this.searchAbort.abort();
      const controller = new AbortController();
      this.searchAbort = controller;
      this.lastQuery = query;
      this.searchResults.setAttribute('aria-busy', 'true');

      const params = new URLSearchParams({
        q: query,
        section_id: RESULTS_SECTION,
        'resources[type]': 'product,article,page',
        'resources[limit]': '10',
        'resources[limit_scope]': 'each',
        'resources[options][fields]': 'title,product_type,variants.title,vendor,body,tag',
        'resources[options][unavailable_products]': 'last',
      });

      try {
        const response = await fetch(`${this.predictiveUrl}?${params.toString()}`, {
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Search failed: ${response.status}`);
        const html = await response.text();
        if (query !== this.searchInput.value.trim()) return;

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const body = doc.querySelector('[data-bbm-results-body]');
        this.searchResults.replaceChildren(body ? document.importNode(body, true) : '');
        const status = this.searchResults.querySelector('[data-bbm-results-status]');
        this.searchStatus.textContent = status ? status.textContent.trim() : '';
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.showSearchError(query);
      } finally {
        // A newer request may still be running; leave its busy state alone.
        if (this.searchAbort === controller) this.searchResults.removeAttribute('aria-busy');
      }
    }

    showSearchError(query) {
      const wrap = document.createElement('div');
      wrap.className = 'bbm-results__empty';
      const text = document.createElement('p');
      text.className = 'bbm-results__empty-text';
      text.textContent = 'Live results didn’t load. ';
      const link = document.createElement('a');
      link.href = `${this.searchForm.getAttribute('action')}?q=${encodeURIComponent(query)}&type=product&options%5Bprefix%5D=last`;
      link.textContent = 'See all results';
      text.append(link);
      wrap.append(text);
      this.searchResults.replaceChildren(wrap);
    }

    // ---- Theme editor --------------------------------------------------------

    onSectionSelect(event) {
      if (event.detail.sectionId === this.sectionId) this.open({ instant: true });
    }

    onSectionDeselect(event) {
      if (event.detail.sectionId === this.sectionId) this.close({ instant: true });
    }

    onBlockSelect(event) {
      if (event.detail.sectionId !== this.sectionId) return;
      this.open({ instant: true });
      event.target.scrollIntoView({ block: 'center' });
    }
  }

  customElements.define('bb-mobile-menu', BbMobileMenu);
})();
